import { GiftOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Flex, Form } from 'antd'
import { useWatch } from 'antd/es/form/Form'
import { GoldenTicketType } from 'Modules'
import { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { logError, logInfo } from 'utils/logger'

import { RarityItemSetup } from './rarity-item-setup'

interface CreateRewardForm {
  pto: string
  ownerAddress: string
  rarities: Array<
    GoldenTicketType & {
      amount: number
      ticket: number
      activationDate: Date
    }
  >
}

export const RaritiesSetup = () => {
  const form = Form.useFormInstance<CreateRewardForm>()
  const ptoId = useWatch('pto', form)
  const [tickets, setTickets] = useState<GoldenTicketType[]>([])

  const getPTODetail = useCallback(
    async (ptoId: string) => {
      try {
        const detailResponse = await AppAdmin.getPTODetail(ptoId)
        logInfo(detailResponse.data.data)
        if (detailResponse.data.success) {
          const tickets = detailResponse.data.data.goldenTickets

          setTickets(tickets)
          // Initialize form list with empty values for each ticket
          form.setFieldsValue({
            rarities: tickets.map((tickets: GoldenTicketType) => ({
              ...tickets,
              ticket: undefined,
              amount: undefined,
              activationDate: undefined,
            })),
          })
        }
      } catch (error) {
        setTickets([])
        form.setFieldsValue({ rarities: [] })
        logError('Error fetching details: ', error)
      }
    },
    [form]
  )

  useEffect(() => {
    if (!ptoId) {
      setTickets([])
      return
    }

    getPTODetail(ptoId)
  }, [ptoId, getPTODetail])

  if (!tickets.length) {
    return (
      <Flex align="center" justify="center" className="rewards-create__empty-state">
        <Flex vertical align="center" gap={8}>
          <GiftOutlined className="rewards-create__empty-state-icon" />
          <div className="rewards-create__empty-state-text">
            Please select a PTO to configure rewards
          </div>
        </Flex>
      </Flex>
    )
  }

  return (
    <div>
      <Flex justify="space-between" align="center">
        <h3>Configure Rewards for Ticket Programs</h3>
        <Flex gap={4} className="rewards-create__info-badge">
          <InfoCircleOutlined className="rewards-create__info-badge-icon" />
          Leave fields empty to skip a program
        </Flex>
      </Flex>
      <Form.List name="rarities">
        {() =>
          tickets.map((ticket, index) => (
            <RarityItemSetup key={ticket.rarityId ?? index} name={index} ticket={ticket} />
          ))
        }
      </Form.List>
    </div>
  )
}
